"use client";

import { useState } from "react";
import Link from "next/link";
import { tourDayForProfile, tipForDay } from "../lib/trial-tour";

// TrialCountdownBanner — feature-tour tip card during the 14-day
// trial. Label is the feature name; copy is the day's tip. The
// sticky bottom bar handles the countdown and subscribe CTA,
// so this stays calm all the way through.
//
// Rendered via <BannerSlot /> when the banner-registry's condition
// matches (seat_type='trial' AND trial_ends_at in the future).

function dismissKey(dayNumber) {
  const today = new Date().toISOString().slice(0, 10);
  return `banner_dismissed_trial_countdown_${today}_d${dayNumber}`;
}

export default function TrialCountdownBanner({ profile }) {
  const day = tourDayForProfile(profile);
  const storageKey = day ? dismissKey(day) : null;
  const initialHidden = (() => {
    if (typeof window === "undefined" || !storageKey) return false;
    try { return localStorage.getItem(storageKey) === "1"; } catch { return false; }
  })();
  const [hidden, setHidden] = useState(initialHidden);

  if (!day || hidden) return null;

  const tipEntry = tipForDay(day);
  if (!tipEntry) return null;

  return (
    <div
      role="complementary"
      aria-label={`${tipEntry.feature} tip`}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "var(--space-3)",
        padding: "var(--space-3) var(--space-4)",
        background: "var(--color-bg-elevated)",
        border: "1px solid var(--color-border-light)",
        borderRadius: "var(--radius-md)",
        marginBottom: "var(--space-4)",
        fontFamily: "var(--font-primary)",
      }}
    >
      <div style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
        <span style={{
          fontSize: "var(--font-size-xs)",
          fontWeight: "var(--font-weight-medium)",
          color: "var(--color-text-muted)",
          letterSpacing: "0.05em",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
          flexShrink: 0,
        }}>
          {tipEntry.feature}
        </span>
        <span style={{ fontSize: "var(--font-size-sm)", color: "var(--color-text)", lineHeight: 1.5 }}>
          {tipEntry.tip}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-2)", flexShrink: 0 }}>
        <Link
          href={tipEntry.href}
          style={{
            display: "inline-block",
            padding: "var(--space-2) var(--space-4)",
            background: "var(--color-text)",
            color: "var(--color-bg)",
            borderRadius: "var(--radius-sm)",
            fontSize: "var(--font-size-sm)",
            fontWeight: "var(--font-weight-medium)",
            textDecoration: "none",
            whiteSpace: "nowrap",
          }}
        >
          {tipEntry.cta}
        </Link>
        <button
          type="button"
          aria-label="Dismiss tip for today"
          onClick={() => {
            try { if (storageKey) localStorage.setItem(storageKey, "1"); } catch {}
            setHidden(true);
          }}
          style={{
            width: 28,
            height: 28,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
            background: "transparent",
            border: "none",
            color: "var(--color-text-secondary)",
            fontSize: "var(--font-size-xl)",
            fontWeight: "var(--font-weight-medium)",
            cursor: "pointer",
            lineHeight: 1,
            borderRadius: "var(--radius-sm)",
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
